// src/ui/utils/visibility.ts — MOVE-08 pause-on-backgrounding wiring.
// Attaches a document.visibilitychange listener to a Phaser.Game instance
// so the ticker stops when the user backgrounds the tab / app and resumes
// cleanly on return. Returns a disposer for useEffect cleanup.

import type Phaser from 'phaser';

/**
 * Attaches document.visibilitychange handling to a Phaser.Game — MOVE-08.
 * Returns a disposer. On background: pauses the game loop. On foreground: resumes.
 *
 * Phaser 4 uses `game.loop.sleep()` / `game.loop.wake()` (formerly pause/resume
 * in Phaser 3). If the Phaser 4 runtime uses different method names we fall
 * back to `pause`/`resume` to stay compatible.
 */
export function attachVisibilityPause(game: Phaser.Game): () => void {
  function onVisibility(): void {
    const loop = game.loop as unknown as {
      sleep?: () => void;
      wake?: () => void;
      pause?: () => void;
      resume?: () => void;
    };
    if (document.hidden) {
      if (typeof loop.sleep === 'function') loop.sleep();
      else if (typeof loop.pause === 'function') loop.pause();
    } else {
      if (typeof loop.wake === 'function') loop.wake();
      else if (typeof loop.resume === 'function') loop.resume();
    }
  }
  document.addEventListener('visibilitychange', onVisibility);
  return () => {
    document.removeEventListener('visibilitychange', onVisibility);
  };
}
